import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { declarationsAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useData } from '../../context/DataContext';
import { getStudentCode } from '../../utils/studentCode';
import Modal from '../../components/Modal';
import RichTextEditor from '../../components/RichTextEditor';
import SignaturePad from '../../components/SignaturePad';
import './Declarations.css';

const emptyTemplateForm = {
  name: '',
  code: '',
  description: '',
  location: 'all',
  headerTemplate: '',
  bodyTemplate: '',
  footerTemplate: '',
  customStyles: '',
  showTitle: true,
  showDefaultSignatures: true,
  signatureSlots: [],
  fields: []
};

const slugifySlotKey = (label, index) => {
  const base = String(label || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '');
  return base || `handtekening${index + 1}`;
};

const fieldTypes = ['text', 'date', 'number', 'select'];

const defaultTemplateFields = [
  { key: 'studyName', label: 'Studierichting', type: 'text', required: true, options: [] },
  { key: 'studyYear', label: 'Studiejaar', type: 'text', required: true, options: [] },
  { key: 'studentNumber', label: 'Studentnummer', type: 'text', required: true, options: [] },
  { key: 'dateFrom', label: 'Periode vanaf', type: 'date', required: false, options: [] },
  { key: 'dateTo', label: 'Periode tot', type: 'date', required: false, options: [] }
];

const defaultBodyTemplate = `<p>De opleidingsdirectie van <strong>Netherlands Business Academy</strong> verklaart dat:</p>
<p><strong>{{student.fullName}}</strong>, geboren op <strong>{{student.dateOfBirthLong}}</strong> te <strong>{{student.placeOfBirth}}</strong>,
met goed gevolg heeft voldaan aan de eisen gesteld aan het curriculum van de opleiding <strong>{{data.studyName}}</strong>.</p>
<p>Studentnummer: <strong>{{data.studentNumber}}</strong> · Studiejaar: <strong>{{data.studyYear}}</strong></p>
<p>Uitgereikt op <strong>{{request.createdAtLong}}</strong>.</p>`;

const placeholderHelpText = 'Placeholders: {{student.fullName}}, {{student.studentCode}}, {{student.initialsName}}, {{student.dateOfBirthLong}}, {{student.placeOfBirth}}, {{request.createdAtLong}}, {{today}}, {{org.logoWide}}, {{data.<veldnaam>}} (datumvelden worden automatisch als "27 juli 2026" geschreven)';

const MODE_CONFIG = {
  student_request: {
    title: 'Verklaringen aanvragen',
    intro: 'Templates die studenten zelf via het studentportaal kunnen aanvragen. Aanvragen worden hier beoordeeld.',
    templatesHeading: 'Templates voor studentaanvragen',
    listTab: 'Aanvragen'
  },
  administrative: {
    title: 'Verklaringen administratie',
    intro: 'Interne verklaringen die studenten niet kunnen aanvragen. Wijs een verklaring toe aan een student en print of download het document.',
    templatesHeading: 'Interne (print) templates',
    listTab: 'Toewijzingen'
  }
};

const todayIso = () => new Date().toISOString().slice(0, 10);

const formatDate = (value) => (value ? new Date(value).toLocaleDateString('nl-NL') : '-');

const AdminDeclarations = ({ mode = 'student_request' }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { data } = useData();
  const isAdministrative = mode === 'administrative';
  const config = MODE_CONFIG[mode] || MODE_CONFIG.student_request;

  const [templates, setTemplates] = useState([]);
  const [requests, setRequests] = useState([]);
  const [activeTab, setActiveTab] = useState(isAdministrative ? 'requests' : 'templates');

  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState(emptyTemplateForm);
  const [templateMode, setTemplateMode] = useState('simple');
  const [newField, setNewField] = useState({ key: '', label: '', type: 'text', required: true, options: '' });

  const [requestSigner, setRequestSigner] = useState({});
  const [listSearch, setListSearch] = useState('');

  // Toewijzing (alleen intern)
  const [assignment, setAssignment] = useState({ templateId: '', studentId: '', requestedData: {}, signedByName: '', signatures: {} });
  const [editingSignatures, setEditingSignatures] = useState({});
  const [removedSignatureKeys, setRemovedSignatureKeys] = useState([]);
  const [studentSearch, setStudentSearch] = useState('');
  const [assignmentCohort, setAssignmentCohort] = useState('');
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [listCohort, setListCohort] = useState('');
  const [listStatus, setListStatus] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [editingAssignmentData, setEditingAssignmentData] = useState({});

  // Voorbeeldvenster: source = { template | templateId, requestedData, title }
  const [preview, setPreview] = useState(null);
  const [previewStudentId, setPreviewStudentId] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);

  const loadData = async () => {
    const [tpl, req] = await Promise.all([declarationsAPI.getTemplates(), declarationsAPI.getRequests()]);
    setTemplates(tpl.templates || []);
    setRequests(req.requests || []);
  };

  useEffect(() => {
    loadData();
  }, []);

  const isOfMode = (type) => (type || 'student_request') === mode;

  const visibleTemplates = useMemo(() => templates.filter((tpl) => isOfMode(tpl.type)), [templates, mode]); // eslint-disable-line react-hooks/exhaustive-deps

  const visibleRequests = useMemo(() => {
    const query = listSearch.trim().toLowerCase();
    return requests
      .filter((req) => isOfMode(req.template?.type))
      .filter((req) => !listCohort || String(req.student?.cohort?.id || '') === String(listCohort))
      .filter((req) => !listStatus || req.status === listStatus)
      .filter((req) => {
        if (!query) return true;
        return [req.template?.name, req.student?.name, req.student?.email, req.student?.studentCode]
          .join(' ')
          .toLowerCase()
          .includes(query);
      });
  }, [requests, mode, listSearch, listCohort, listStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  const pendingCount = useMemo(
    () => requests.filter((req) => isOfMode(req.template?.type) && req.status === 'pending').length,
    [requests, mode] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const cohortOptions = useMemo(() => {
    let cohorts = data.cohorts || [];
    if (user?.role === 'manager' && user.location) {
      cohorts = cohorts.filter((c) => String(c.location || '').toLowerCase() === String(user.location).toLowerCase());
    }
    return [...cohorts].sort((a, b) => (a.name || a.code || '').localeCompare(b.name || b.code || '', 'nl'));
  }, [data.cohorts, user]);

  const assignableStudents = useMemo(() => {
    let students = data.students || [];
    if (user?.role === 'manager' && user.location) {
      students = students.filter((s) => String(s.location || '').toLowerCase() === String(user.location).toLowerCase());
    }
    if (assignmentCohort) {
      students = students.filter((s) => String(s.cohort?.id || s.cohort?._id || '') === String(assignmentCohort));
    }
    const query = studentSearch.trim().toLowerCase();
    if (query) {
      students = students.filter((s) => `${s.name || ''} ${s.email || ''} ${getStudentCode(s)}`.toLowerCase().includes(query));
    }
    return [...students].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'nl'));
  }, [data.students, studentSearch, user, assignmentCohort]);

  const assignmentTemplate = visibleTemplates.find((tpl) => String(tpl.id) === String(assignment.templateId));
  const assignmentStudent = (data.students || []).find((s) => String(s.id || s._id) === String(assignment.studentId));

  const buildDefaultRequestedData = (template, student) => {
    const defaults = {};
    (template?.fields || []).forEach((field) => {
      if (field.key === 'studyName') defaults.studyName = student?.studentProfile?.opleiding || '';
      else if (/^(studentNumber|studentCode)$/i.test(field.key)) defaults[field.key] = getStudentCode(student);
      else if (field.type === 'date' && /issue|uitreik|datum/i.test(field.key)) defaults[field.key] = todayIso();
      else defaults[field.key] = '';
    });
    return defaults;
  };

  const updateAssignmentSelection = (updates) => {
    setAssignment((prev) => {
      const next = { ...prev, ...updates };
      const template = visibleTemplates.find((tpl) => String(tpl.id) === String(next.templateId));
      const student = (data.students || []).find((s) => String(s.id || s._id) === String(next.studentId));
      const defaults = buildDefaultRequestedData(template, student);
      // Behoud wat al is ingevuld, vul ontbrekende velden aan met standaardwaarden.
      // Bij een andere student worden studentgebonden velden (opleiding, studentcode) opnieuw ingevuld.
      const studentChanged = updates.studentId !== undefined && String(updates.studentId) !== String(prev.studentId);
      const studentBound = /^(studyName|studentNumber|studentCode)$/i;
      const requestedData = Object.keys(defaults).reduce((acc, key) => {
        acc[key] = (studentChanged && studentBound.test(key))
          ? defaults[key]
          : (prev.requestedData?.[key] || defaults[key]);
        return acc;
      }, {});
      return { ...next, requestedData };
    });
  };

  const openCreateTemplateModal = () => {
    setEditingTemplate(null);
    setTemplateMode('simple');
    setTemplateForm({
      ...emptyTemplateForm,
      type: mode,
      headerTemplate: '<div style="display:flex;flex-direction:column;gap:6px;align-items:flex-start;"><div style="font-size:24px;font-weight:700;letter-spacing:0.5px;">Netherlands Business Academy</div><div style="font-size:12px;">Suriname</div><div style="font-size:11px;color:#4b5563;">Registratiecode: {{template.code}} · {{request.createdAt}}</div></div>',
      bodyTemplate: defaultBodyTemplate,
      footerTemplate: '<div style="display:flex;justify-content:space-between;align-items:flex-end;gap:16px;flex-wrap:wrap; margin-top:28px;"><div><div style="border-top:1px solid #111827; padding-top:8px; min-width:180px;">R. Soekhai</div><small>Directeur</small></div><div><div style="border-top:1px solid #111827; padding-top:8px; min-width:180px;">E. Audhoe MBA</div><small>Examencommissie</small></div><div><small>Reg-{{template.code}}-{{request.id}}</small></div></div>',
      showDefaultSignatures: false,
      fields: defaultTemplateFields
    });
    setNewField({ key: '', label: '', type: 'text', required: true, options: '' });
    setShowTemplateModal(true);
  };

  // Alleen admin: template verwijderen; gekoppelde aanvragen/toewijzingen gaan na bevestiging mee.
  const handleDeleteTemplate = async (template) => {
    const linkedCount = requests.filter((req) => String(req.template?.id) === String(template.id)).length;
    const linkedLabel = isAdministrative ? 'toewijzing(en)' : 'aanvraag/aanvragen';
    const message = linkedCount
      ? `Template "${template.name}" verwijderen?\n\nEr zijn ${linkedCount} ${linkedLabel} met deze template. Die worden ook verwijderd, inclusief de gegenereerde documenten. Dit kan niet ongedaan worden gemaakt.`
      : `Template "${template.name}" verwijderen? Dit kan niet ongedaan worden gemaakt.`;
    if (!window.confirm(message)) return;

    try {
      await declarationsAPI.deleteTemplate(template.id, { deleteRequests: linkedCount > 0 });
      await loadData();
    } catch (error) {
      window.alert(error.message || 'Template verwijderen mislukt');
    }
  };

  const openEditTemplateModal = (template) => {
    setEditingTemplate(template);
    setTemplateMode('advanced');
    setTemplateForm({
      name: template.name || '',
      code: template.code || '',
      description: template.description || '',
      location: template.location || 'all',
      type: template.type || 'student_request',
      headerTemplate: template.headerTemplate || '',
      bodyTemplate: template.bodyTemplate || '',
      footerTemplate: template.footerTemplate || '',
      customStyles: template.customStyles || '',
      showTitle: template.showTitle !== false,
      showDefaultSignatures: template.showDefaultSignatures !== false,
      // Bestaande vakken: sleutel vastzetten, zodat placeholders in de tekst blijven werken.
      signatureSlots: (Array.isArray(template.signatureSlots) ? template.signatureSlots : []).map((slot) => ({ ...slot, keyLocked: true })),
      fields: Array.isArray(template.fields) ? template.fields : []
    });
    setNewField({ key: '', label: '', type: 'text', required: true, options: '' });
    setShowTemplateModal(true);
  };

  const addField = () => {
    const key = newField.key.trim();
    const label = (newField.label || '').trim() || key;
    if (!key) return;

    if (templateForm.fields.some((field) => field.key === key)) {
      window.alert('Dit veld bestaat al in deze template.');
      return;
    }

    const options = newField.type === 'select'
      ? (newField.options || '').split(',').map((item) => item.trim()).filter(Boolean)
      : [];

    setTemplateForm((prev) => ({
      ...prev,
      fields: [...prev.fields, { key, label, type: newField.type, required: !!newField.required, options }]
    }));

    setNewField({ key: '', label: '', type: 'text', required: true, options: '' });
  };

  const removeField = (key) => {
    setTemplateForm((prev) => ({ ...prev, fields: prev.fields.filter((field) => field.key !== key) }));
  };

  const updateField = (key, updates) => {
    setTemplateForm((prev) => ({
      ...prev,
      fields: (prev.fields || []).map((field) => (field.key === key ? { ...field, ...updates } : field))
    }));
  };

  const handleSaveTemplate = async (e) => {
    e.preventDefault();

    const payload = {
      ...templateForm,
      type: mode,
      code: (templateForm.code || '').toUpperCase().trim()
    };

    try {
      if (editingTemplate?.id) {
        await declarationsAPI.updateTemplate(editingTemplate.id, payload);
      } else {
        await declarationsAPI.createTemplate(payload);
      }
    } catch (error) {
      window.alert(error.message || 'Template opslaan mislukt');
      return;
    }

    setShowTemplateModal(false);
    setEditingTemplate(null);
    setTemplateForm(emptyTemplateForm);
    await loadData();
  };

  const handleReview = async (requestId, status) => {
    const signedByName = requestSigner[requestId] || 'NLBA Staff';

    await declarationsAPI.reviewRequest(requestId, {
      status,
      signedByName,
      signatureData: 'digital-signature-placeholder'
    });

    await loadData();
  };

  const missingRequiredFields = (template, values) => (template?.fields || [])
    .filter((field) => field.required && !String(values?.[field.key] ?? '').trim())
    .map((field) => field.label || field.key);

  const handleCreateAssignment = async (e) => {
    e.preventDefault();
    if (!assignment.templateId || !assignment.studentId) {
      window.alert('Selecteer eerst een template en een student.');
      return;
    }

    const missing = missingRequiredFields(assignmentTemplate, assignment.requestedData);
    if (missing.length) {
      window.alert(`Vul de verplichte velden in: ${missing.join(', ')}`);
      return;
    }

    setAssigning(true);
    try {
      const created = await declarationsAPI.createRequest({
        templateId: assignment.templateId,
        studentId: assignment.studentId,
        requestedData: assignment.requestedData,
        signedByName: assignment.signedByName || undefined,
        signatures: signaturesToPayload(assignment.signatures)
      });
      setAssignment({ templateId: assignment.templateId, studentId: '', requestedData: {}, signedByName: assignment.signedByName, signatures: {} });
      setStudentSearch('');
      setShowAssignModal(false);
      await loadData();
      if (created?.id && window.confirm('Verklaring is toegewezen en gegenereerd. Nu printen?')) {
        await handlePrintDocument(created.id);
      }
    } catch (error) {
      window.alert(error.message || 'Toewijzen mislukt');
    } finally {
      setAssigning(false);
    }
  };

  const signaturesToPayload = (signatures = {}) => Object.entries(signatures)
    .filter(([, imageData]) => Boolean(imageData))
    .map(([key, imageData]) => ({ key, imageData }));

  const openEditAssignment = (req) => {
    const template = templates.find((tpl) => String(tpl.id) === String(req.template?.id));
    setEditingAssignment({ ...req, fields: template?.fields || [], signatureSlots: template?.signatureSlots || [] });
    setEditingAssignmentData({ ...(req.requestedData || {}) });
    setEditingSignatures({});
    setRemovedSignatureKeys([]);
  };

  const handleRegenerate = async (e) => {
    e.preventDefault();
    if (!editingAssignment) return;
    try {
      await declarationsAPI.regenerateDocument(editingAssignment.id, {
        requestedData: editingAssignmentData,
        signatures: signaturesToPayload(editingSignatures),
        removedSignatureKeys
      });
      setEditingAssignment(null);
      await loadData();
    } catch (error) {
      window.alert(error.message || 'Opnieuw genereren mislukt');
    }
  };

  const handleViewDocument = async (requestId) => {
    const blob = await declarationsAPI.getDocumentBlob(requestId, 'view');
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank', 'noopener,noreferrer');
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  };

  const handleDownloadDocument = async (requestId, fileLabel = 'declaration') => {
    const blob = await declarationsAPI.getDocumentBlob(requestId, 'download');
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${fileLabel.replace(/[^\w-]+/g, '_')}_${requestId}.html`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Print via een verborgen iframe zodat het document direct in de printdialoog opent.
  const handlePrintDocument = async (requestId) => {
    const blob = await declarationsAPI.getDocumentBlob(requestId, 'view');
    const url = URL.createObjectURL(blob);
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    iframe.src = url;
    iframe.onload = () => {
      setTimeout(() => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        setTimeout(() => {
          document.body.removeChild(iframe);
          URL.revokeObjectURL(url);
        }, 60000);
      }, 400);
    };
    document.body.appendChild(iframe);
  };

  const loadPreview = async (source, studentId) => {
    setPreviewLoading(true);
    try {
      const { html } = await declarationsAPI.previewTemplate({
        template: source.template,
        templateId: source.templateId,
        requestedData: source.requestedData || {},
        signatures: source.signatures || [],
        studentId: studentId || undefined
      });
      setPreview({ ...source, html });
    } catch (error) {
      window.alert(error.message || 'Voorbeeld laden mislukt');
    } finally {
      setPreviewLoading(false);
    }
  };

  const openPreview = (source, studentId = '') => {
    setPreviewStudentId(studentId);
    loadPreview(source, studentId);
  };

  const previewTemplateForm = () => openPreview({
    title: `Voorbeeld: ${templateForm.name || 'nieuwe template'}`,
    template: { ...templateForm, code: (templateForm.code || '').toUpperCase().trim() }
  });

  const previewAssignment = () => {
    if (!assignment.templateId) {
      window.alert('Selecteer eerst een template.');
      return;
    }
    openPreview({
      title: `Voorbeeld: ${assignmentTemplate?.name || ''}`,
      templateId: assignment.templateId,
      requestedData: assignment.requestedData,
      signatures: signaturesToPayload(assignment.signatures)
    }, assignment.studentId);
  };

  const printPreview = () => {
    const iframe = document.getElementById('declaration-preview-frame');
    if (iframe?.contentWindow) {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    }
  };

  const renderFieldInput = (field, values, setValues) => {
    const value = values?.[field.key] ?? '';
    const onFieldChange = (e) => setValues({ ...values, [field.key]: e.target.value });

    if (field.type === 'select') {
      return (
        <select className="form-input" value={value} onChange={onFieldChange} required={field.required}>
          <option value="">Selecteer…</option>
          {(field.options || []).map((option) => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
      );
    }

    return (
      <input
        className="form-input"
        type={field.type === 'date' ? 'date' : field.type === 'number' ? 'number' : 'text'}
        value={value}
        onChange={onFieldChange}
        required={field.required}
        placeholder={field.label}
      />
    );
  };

  const openAssignModal = (templateId = '') => {
    setAssignment((prev) => ({ templateId, studentId: '', requestedData: {}, signedByName: prev.signedByName, signatures: {} }));
    setStudentSearch('');
    if (templateId) {
      const template = visibleTemplates.find((tpl) => String(tpl.id) === String(templateId));
      setAssignment((prev) => ({ ...prev, requestedData: buildDefaultRequestedData(template, null) }));
    }
    setShowAssignModal(true);
  };

  const cohortLabel = (cohort) => {
    if (!cohort) return '-';
    const name = cohort.name || '';
    const code = cohort.code || '';
    if (name && code && !name.includes(code)) return `${name} (${code})`;
    return name || code || '-';
  };

  const statusLabel = { pending: 'Open', approved: 'Goedgekeurd', rejected: 'Afgewezen' };

  const renderListFilters = (withStatus) => (
    <div className="decl-filters">
      <input
        className="form-input"
        type="search"
        value={listSearch}
        onChange={(e) => setListSearch(e.target.value)}
        placeholder="Zoek op student, e-mail of verklaring…"
      />
      <select className="form-input" value={listCohort} onChange={(e) => setListCohort(e.target.value)}>
        <option value="">Alle cohorten</option>
        {cohortOptions.map((cohort) => (
          <option key={cohort.id || cohort._id} value={cohort.id || cohort._id}>{cohortLabel(cohort)}</option>
        ))}
      </select>
      {withStatus && (
        <select className="form-input" value={listStatus} onChange={(e) => setListStatus(e.target.value)}>
          <option value="">Alle statussen</option>
          <option value="pending">Open</option>
          <option value="approved">Goedgekeurd</option>
          <option value="rejected">Afgewezen</option>
        </select>
      )}
      {(listSearch || listCohort || listStatus) && (
        <button type="button" className="btn-secondary" onClick={() => { setListSearch(''); setListCohort(''); setListStatus(''); }}>
          Reset
        </button>
      )}
    </div>
  );

  const renderTemplatesTab = () => (
    <div className="decl-template-grid">
      {visibleTemplates.length === 0 && <p className="decl-empty">Nog geen templates. Voeg er een toe of importeer de JSON templates.</p>}
      {visibleTemplates.map((tpl) => (
        <div key={tpl.id} className="decl-card">
          <div className="decl-card-body">
            <div className="decl-card-title">
              <h3>{tpl.name}</h3>
              <span className="decl-code">{tpl.code}</span>
            </div>
            <p className="decl-muted">{tpl.description || 'Geen omschrijving'}</p>
            <div className="decl-meta">
              <span><strong>Locatie:</strong> {tpl.location === 'all' ? 'Alle locaties' : tpl.location}</span>
              <span><strong>Velden:</strong> {(tpl.fields || []).map((field) => field.label || field.key).join(', ') || '-'}</span>
            </div>
          </div>
          <div className="decl-card-actions">
            {isAdministrative && (
              <button type="button" className="btn-primary" onClick={() => openAssignModal(tpl.id)}>Gebruiken</button>
            )}
            <button
              type="button"
              className="btn-secondary"
              onClick={() => openPreview({ title: `Voorbeeld: ${tpl.name}`, templateId: tpl.id })}
            >
              Voorbeeld
            </button>
            <button type="button" className="btn-secondary" onClick={() => openEditTemplateModal(tpl)}>Bewerken</button>
            {user?.role === 'admin' && (
              <button type="button" className="btn-delete" onClick={() => handleDeleteTemplate(tpl)}>Verwijderen</button>
            )}
          </div>
        </div>
      ))}
    </div>
  );

  const renderStudentRequestsTab = () => (
    <>
      {renderListFilters(true)}
      <div className="decl-table-wrap">
        <table className="decl-table">
          <thead>
            <tr>
              <th>Student</th>
              <th>Cohort</th>
              <th>Verklaring</th>
              <th>Aangevraagd op</th>
              <th>Status</th>
              <th className="decl-actions-col">Acties</th>
            </tr>
          </thead>
          <tbody>
            {visibleRequests.length === 0 && (
              <tr><td colSpan={6} className="decl-empty">Geen aanvragen gevonden.</td></tr>
            )}
            {visibleRequests.map((req) => (
              <tr key={req.id}>
                <td>
                  <div className="decl-student">{req.student?.name || '-'}</div>
                  <div className="decl-muted">{req.student?.email}</div>
                </td>
                <td>{cohortLabel(req.student?.cohort)}</td>
                <td>{req.template?.name}</td>
                <td>{formatDate(req.createdAt)}</td>
                <td><span className={`decl-status decl-status-${req.status}`}>{statusLabel[req.status] || req.status}</span></td>
                <td className="decl-actions-col">
                  {req.status === 'pending' ? (
                    <div className="decl-row-actions">
                      <input
                        className="form-input decl-signer"
                        value={requestSigner[req.id] || ''}
                        onChange={(e) => setRequestSigner((prev) => ({ ...prev, [req.id]: e.target.value }))}
                        placeholder="Ondertekenaar (optioneel)"
                      />
                      <button className="btn-primary" onClick={() => handleReview(req.id, 'approved')}>Goedkeuren</button>
                      <button className="btn-delete" onClick={() => handleReview(req.id, 'rejected')}>Afwijzen</button>
                    </div>
                  ) : req.generatedDocument ? (
                    <div className="decl-row-actions">
                      <button type="button" className="btn-primary" onClick={() => handlePrintDocument(req.id)}>Printen</button>
                      <button type="button" className="btn-secondary" onClick={() => handleViewDocument(req.id)}>Bekijken</button>
                      <button type="button" className="btn-secondary" onClick={() => handleDownloadDocument(req.id, req.template?.name)}>Download</button>
                    </div>
                  ) : (
                    <span className="decl-muted">-</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );

  const renderAssignmentsTab = () => (
    <>
      {renderListFilters(false)}
      <div className="decl-table-wrap">
        <table className="decl-table">
          <thead>
            <tr>
              <th>Student</th>
              <th>Cohort</th>
              <th>Verklaring</th>
              <th>Toegewezen op</th>
              <th>Door</th>
              <th className="decl-actions-col">Acties</th>
            </tr>
          </thead>
          <tbody>
            {visibleRequests.length === 0 && (
              <tr>
                <td colSpan={6} className="decl-empty">
                  Nog geen toewijzingen.{' '}
                  <button type="button" className="decl-link" onClick={() => openAssignModal()}>Maak de eerste toewijzing</button>
                </td>
              </tr>
            )}
            {visibleRequests.map((req) => (
              <tr key={req.id}>
                <td>
                  <div className="decl-student">{req.student?.name || '-'}</div>
                  <div className="decl-muted">{req.student?.email}</div>
                </td>
                <td>{cohortLabel(req.student?.cohort)}</td>
                <td>{req.template?.name}</td>
                <td>{formatDate(req.createdAt)}</td>
                <td>{req.assignedBy?.name || req.reviewedBy?.name || '-'}</td>
                <td className="decl-actions-col">
                  <div className="decl-row-actions">
                    {req.generatedDocument && (
                      <>
                        <button type="button" className="btn-primary" onClick={() => handlePrintDocument(req.id)}>Printen</button>
                        <button type="button" className="btn-secondary" onClick={() => handleDownloadDocument(req.id, req.template?.name)}>Download</button>
                        <button type="button" className="btn-secondary" onClick={() => handleViewDocument(req.id)}>Bekijken</button>
                      </>
                    )}
                    {req.status === 'approved' && (
                      <button type="button" className="btn-secondary" onClick={() => openEditAssignment(req)}>Aanpassen</button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );

  const renderAssignForm = () => (
    <form onSubmit={handleCreateAssignment} className="decl-assign-form">
      <div className="decl-form-grid">
        <label className="decl-field decl-field-full">
          <span>Template *</span>
          <select
            className="form-input"
            value={assignment.templateId}
            onChange={(e) => updateAssignmentSelection({ templateId: e.target.value })}
            required
          >
            <option value="">Selecteer template</option>
            {visibleTemplates.map((tpl) => (
              <option key={tpl.id} value={tpl.id}>{tpl.name}</option>
            ))}
          </select>
        </label>

        <label className="decl-field">
          <span>Cohort</span>
          <select
            className="form-input"
            value={assignmentCohort}
            onChange={(e) => {
              setAssignmentCohort(e.target.value);
              updateAssignmentSelection({ studentId: '' });
            }}
          >
            <option value="">Alle cohorten</option>
            {cohortOptions.map((cohort) => (
              <option key={cohort.id || cohort._id} value={cohort.id || cohort._id}>{cohortLabel(cohort)}</option>
            ))}
          </select>
        </label>

        <label className="decl-field">
          <span>Zoek student</span>
          <input
            className="form-input"
            type="search"
            value={studentSearch}
            onChange={(e) => setStudentSearch(e.target.value)}
            placeholder="Naam, e-mail of studentcode…"
          />
        </label>

        <label className="decl-field decl-field-full">
          <span>Student * <small className="decl-muted">({assignableStudents.length} gevonden)</small></span>
          <select
            className="form-input"
            value={assignment.studentId}
            onChange={(e) => updateAssignmentSelection({ studentId: e.target.value })}
            required
            size={Math.min(6, Math.max(3, assignableStudents.length + 1))}
          >
            <option value="">— Kies een student —</option>
            {assignableStudents.map((student) => (
              <option key={student.id || student._id} value={student.id || student._id}>
                {student.name}{student.email ? ` – ${student.email}` : ''}{student.cohort ? ` · ${cohortLabel(student.cohort)}` : ''}
              </option>
            ))}
          </select>
        </label>
      </div>

      {assignmentStudent && (
        <div className="decl-student-info">
          <strong>{assignmentStudent.name}</strong>
          <span>Geboortedatum: {assignmentStudent.dateOfBirth ? formatDate(assignmentStudent.dateOfBirth) : 'niet ingevuld'}</span>
          <span>Geboorteplaats: {assignmentStudent.studentProfile?.placeOfBirth || 'niet ingevuld'}</span>
          <span>Opleiding: {assignmentStudent.studentProfile?.opleiding || 'niet ingevuld'}</span>
        </div>
      )}

      {assignmentTemplate && (assignmentTemplate.fields || []).length > 0 && (
        <>
          <h4 className="decl-section-title">Gegevens op het document</h4>
          <div className="decl-form-grid">
            {assignmentTemplate.fields.map((field) => (
              <label key={field.key} className="decl-field">
                <span>{field.label || field.key}{field.required ? ' *' : ''}</span>
                {renderFieldInput(field, assignment.requestedData, (values) => setAssignment((prev) => ({ ...prev, requestedData: values })))}
              </label>
            ))}
          </div>
        </>
      )}

      {(assignmentTemplate?.signatureSlots || []).length > 0 && (
        <>
          <h4 className="decl-section-title">Handtekeningen ({assignmentTemplate.signatureSlots.length})</h4>
          <div className="decl-signature-grid">
            {assignmentTemplate.signatureSlots.map((slot) => (
              <SignaturePad
                key={slot.key}
                label={slot.label || slot.key}
                value={assignment.signatures?.[slot.key] || ''}
                onChange={(imageData) => setAssignment((prev) => ({
                  ...prev,
                  signatures: { ...(prev.signatures || {}), [slot.key]: imageData }
                }))}
              />
            ))}
          </div>
        </>
      )}

      <label className="decl-field">
        <span>Uitgereikt / ondertekend door</span>
        <input
          className="form-input"
          value={assignment.signedByName}
          onChange={(e) => setAssignment((prev) => ({ ...prev, signedByName: e.target.value }))}
          placeholder="Optioneel"
        />
      </label>

      <div className="decl-modal-actions">
        <button type="button" className="btn-secondary" onClick={() => setShowAssignModal(false)}>{t('common.cancel')}</button>
        <button type="button" className="btn-secondary" onClick={previewAssignment} disabled={!assignment.templateId || previewLoading}>
          Voorbeeld
        </button>
        <button type="submit" className="btn-primary" disabled={assigning}>
          {assigning ? 'Bezig…' : 'Toewijzen en genereren'}
        </button>
      </div>
    </form>
  );

  const tabs = isAdministrative
    ? [
      { key: 'requests', label: `${config.listTab} (${requests.filter((req) => isOfMode(req.template?.type)).length})` },
      { key: 'templates', label: `Templates (${visibleTemplates.length})` }
    ]
    : [
      { key: 'templates', label: `Templates (${visibleTemplates.length})` },
      { key: 'requests', label: `${config.listTab} (${pendingCount} open)` }
    ];

  return (
    <div className="users-page decl-page">
      <div className="decl-header">
        <div>
          <h1>{config.title}</h1>
          <p className="decl-muted">{config.intro}</p>
        </div>
        <div className="decl-header-actions">
          {isAdministrative && (
            <button type="button" className="btn-primary" onClick={() => openAssignModal()}>+ Nieuwe toewijzing</button>
          )}
          <button
            type="button"
            className={isAdministrative ? 'btn-secondary' : 'btn-primary'}
            onClick={() => { setActiveTab('templates'); openCreateTemplateModal(); }}
          >
            + Nieuwe template
          </button>
        </div>
      </div>

      <div className="decl-tabs" role="tablist">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.key}
            className={`decl-tab ${activeTab === tab.key ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'templates' && renderTemplatesTab()}
      {activeTab === 'requests' && (isAdministrative ? renderAssignmentsTab() : renderStudentRequestsTab())}

      <Modal
        isOpen={showAssignModal}
        onClose={() => setShowAssignModal(false)}
        title="Verklaring toewijzen"
        size="lg"
      >
        {renderAssignForm()}
      </Modal>

      <Modal
        isOpen={!!editingAssignment}
        onClose={() => setEditingAssignment(null)}
        title="Gegevens aanpassen en opnieuw genereren"
      >
        {editingAssignment && (
          <form onSubmit={handleRegenerate} className="add-form">
            <p><strong>{editingAssignment.template?.name}</strong> · {editingAssignment.student?.name}</p>
            {(editingAssignment.fields || []).length === 0 && <p>Deze template heeft geen extra velden.</p>}
            {(editingAssignment.fields || []).map((field) => (
              <div key={field.key} className="form-group">
                <label>{field.label || field.key}{field.required ? ' *' : ''}</label>
                {renderFieldInput(field, editingAssignmentData, setEditingAssignmentData)}
              </div>
            ))}
            {(editingAssignment.signatureSlots || []).length > 0 && (
              <div className="decl-signature-grid">
                {editingAssignment.signatureSlots.map((slot) => {
                  const existing = (editingAssignment.signatures || []).find((sig) => sig.key === slot.key && sig.hasImage);
                  const removed = removedSignatureKeys.includes(slot.key);
                  return (
                    <div key={slot.key}>
                      <SignaturePad
                        label={`${slot.label || slot.key}${existing && !removed && !editingSignatures[slot.key] ? ' (al ondertekend – teken of upload om te vervangen)' : ''}`}
                        value={editingSignatures[slot.key] || ''}
                        onChange={(imageData) => setEditingSignatures((prev) => ({ ...prev, [slot.key]: imageData }))}
                        height={120}
                      />
                      {existing && (
                        <label className="decl-remove-signature">
                          <input
                            type="checkbox"
                            checked={removed}
                            onChange={(e) => setRemovedSignatureKeys((prev) => (
                              e.target.checked ? [...prev, slot.key] : prev.filter((key) => key !== slot.key)
                            ))}
                          />
                          Bestaande handtekening verwijderen
                        </label>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            <div className="form-actions">
              <button className="btn-primary" type="submit">Opslaan en opnieuw genereren</button>
              <button className="btn-secondary" type="button" onClick={() => setEditingAssignment(null)}>{t('common.cancel')}</button>
            </div>
          </form>
        )}
      </Modal>

      <Modal
        isOpen={showTemplateModal}
        onClose={() => setShowTemplateModal(false)}
        title={editingTemplate ? 'Template bewerken' : 'Template aanmaken'}
      >
        <form onSubmit={handleSaveTemplate} className="add-form">
          <div className="form-row" style={{ marginBottom: '6px' }}>
            <button
              type="button"
              className={templateMode === 'simple' ? 'btn-primary' : 'btn-secondary'}
              onClick={() => setTemplateMode('simple')}
            >
              Simpel
            </button>
            <button
              type="button"
              className={templateMode === 'advanced' ? 'btn-primary' : 'btn-secondary'}
              onClick={() => setTemplateMode('advanced')}
            >
              Geavanceerd
            </button>
          </div>
          <small style={{ color: '#4b5563' }}>
            Simpel gebruikt standaardvelden en basisopmaak. Geavanceerd geeft volledige vrijheid.
          </small>

          <div className="form-row">
            <input
              className="form-input"
              value={templateForm.name}
              onChange={(e) => setTemplateForm((prev) => ({ ...prev, name: e.target.value }))}
              placeholder={t('common.name')}
              required
            />
            <input
              className="form-input"
              value={templateForm.code}
              onChange={(e) => setTemplateForm((prev) => ({ ...prev, code: e.target.value }))}
              placeholder="Code"
              required
              disabled={!!editingTemplate}
            />
          </div>

          <div className="form-row">
            <input
              className="form-input"
              value={templateForm.location}
              onChange={(e) => setTemplateForm((prev) => ({ ...prev, location: e.target.value }))}
              placeholder="Locatie"
              list="declaration-template-location-options"
            />
            <textarea
              className="form-textarea"
              value={templateForm.description}
              onChange={(e) => setTemplateForm((prev) => ({ ...prev, description: e.target.value }))}
              placeholder={t('course.description')}
            />
          </div>

          <datalist id="declaration-template-location-options">
            <option value="all" />
            <option value="Paramaribo" />
            <option value="Nickerie" />
          </datalist>

          <div className="form-row">
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="checkbox"
                checked={templateForm.showTitle !== false}
                onChange={(e) => setTemplateForm((prev) => ({ ...prev, showTitle: e.target.checked }))}
              />
              Templatenaam als titel tonen
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="checkbox"
                checked={templateForm.showDefaultSignatures !== false}
                onChange={(e) => setTemplateForm((prev) => ({ ...prev, showDefaultSignatures: e.target.checked }))}
              />
              Standaard handtekeningblok toevoegen
            </label>
          </div>

          <div className="form-group">
            <label><strong>Digitale handtekeningen</strong></label>
            <small style={{ color: '#4b5563', display: 'block', marginBottom: '8px' }}>
              Voeg een vak toe per handtekening (bijv. Directeur, Examencommissie, Student). Bij het toewijzen wordt per vak getekend of een afbeelding geüpload.
              Plaats de handtekening in de tekst met de getoonde placeholder; zonder placeholder komt die automatisch onderaan het document.
            </small>
            {(templateForm.signatureSlots || []).map((slot, index) => (
              <div key={`${slot.key}-${index}`} className="decl-slot-row">
                <input
                  className="form-input"
                  value={slot.label}
                  placeholder="Omschrijving (bijv. Directeur)"
                  onChange={(e) => {
                    const label = e.target.value;
                    setTemplateForm((prev) => ({
                      ...prev,
                      signatureSlots: prev.signatureSlots.map((item, i) => (
                        i === index ? { ...item, label, key: item.keyLocked ? item.key : slugifySlotKey(label, i) } : item
                      ))
                    }));
                  }}
                />
                <code className="decl-slot-placeholder">{`{{signature.${slot.key}}}`}</code>
                <button
                  type="button"
                  className="btn-delete"
                  onClick={() => setTemplateForm((prev) => ({ ...prev, signatureSlots: prev.signatureSlots.filter((_, i) => i !== index) }))}
                >
                  Verwijder
                </button>
              </div>
            ))}
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setTemplateForm((prev) => {
                const slots = prev.signatureSlots || [];
                return { ...prev, signatureSlots: [...slots, { key: `handtekening${slots.length + 1}`, label: '' }] };
              })}
            >
              + Handtekeningvak toevoegen
            </button>
          </div>

          <div className="form-group">
            <label><strong>{isAdministrative ? 'Velden bij toewijzing' : 'Dynamische velden voor aanvragen'}</strong></label>
            <small style={{ color: '#4b5563', display: 'block', marginBottom: '8px' }}>
              {isAdministrative
                ? 'Deze velden vul je in wanneer je de verklaring aan een student toewijst. Gebruik ze in de tekst als {{data.veldnaam}}.'
                : 'Deze velden vult de student in bij de aanvraag. Je kunt labels aanpassen en velden verplicht maken.'}
            </small>

            <div style={{ marginBottom: '8px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {(templateForm.fields || []).map((field) => (
                <div key={field.key} style={{ display: 'grid', gridTemplateColumns: '1.2fr 1.2fr 0.8fr 0.8fr auto', gap: '8px', alignItems: 'center' }}>
                  <input
                    className="form-input"
                    value={field.key}
                    disabled
                  />
                  <input
                    className="form-input"
                    value={field.label || ''}
                    onChange={(e) => updateField(field.key, { label: e.target.value })}
                  />
                  <select
                    className="form-input"
                    value={field.type}
                    onChange={(e) => updateField(field.key, { type: e.target.value })}
                  >
                    {fieldTypes.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.9rem' }}>
                    <input
                      type="checkbox"
                      checked={!!field.required}
                      onChange={(e) => updateField(field.key, { required: e.target.checked })}
                    />
                    Verplicht
                  </label>
                  <button type="button" className="btn-delete" onClick={() => removeField(field.key)}>Verwijder</button>
                </div>
              ))}
            </div>

            {templateMode === 'advanced' && (
              <>
            <div className="form-row">
              <input
                className="form-input"
                value={newField.key}
                onChange={(e) => setNewField((prev) => ({ ...prev, key: e.target.value }))}
                placeholder="Veld key (bijv. studyYear)"
              />
              <input
                className="form-input"
                value={newField.label}
                onChange={(e) => setNewField((prev) => ({ ...prev, label: e.target.value }))}
                placeholder="Label"
              />
            </div>
            <div className="form-row">
              <select
                className="form-input"
                value={newField.type}
                onChange={(e) => setNewField((prev) => ({ ...prev, type: e.target.value }))}
              >
                {fieldTypes.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>

              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={!!newField.required}
                  onChange={(e) => setNewField((prev) => ({ ...prev, required: e.target.checked }))}
                />
                Verplicht
              </label>

              <button type="button" className="btn-secondary" onClick={addField}>Veld toevoegen</button>
            </div>
            {newField.type === 'select' && (
              <input
                className="form-input"
                value={newField.options}
                onChange={(e) => setNewField((prev) => ({ ...prev, options: e.target.value }))}
                placeholder="Opties (komma gescheiden)"
              />
            )}
              </>
            )}
          </div>

          {templateMode === 'advanced' && (
            <RichTextEditor
              label="Header (Word-stijl, incl. afbeeldingen)"
              value={templateForm.headerTemplate}
              onChange={(value) => setTemplateForm((prev) => ({ ...prev, headerTemplate: value }))}
              placeholder="Gebruik placeholders zoals {{student.fullName}} en {{request.createdAt}}"
              minHeight={140}
            />
          )}

          <RichTextEditor
            label="Body template"
            value={templateForm.bodyTemplate}
            onChange={(value) => setTemplateForm((prev) => ({ ...prev, bodyTemplate: value }))}
            placeholder="Gebruik placeholders zoals {{student.fullName}}, {{data.studyName}}, {{data.studyYear}}"
            minHeight={240}
          />
          <small style={{ color: '#4b5563' }}>{placeholderHelpText}</small>

          {templateMode === 'advanced' && (
            <>
              <RichTextEditor
                label="Footer"
                value={templateForm.footerTemplate}
                onChange={(value) => setTemplateForm((prev) => ({ ...prev, footerTemplate: value }))}
                placeholder="Bijv. contactinformatie of standaard slottekst"
                minHeight={120}
              />

              <textarea
                className="form-textarea"
                value={templateForm.customStyles}
                onChange={(e) => setTemplateForm((prev) => ({ ...prev, customStyles: e.target.value }))}
                placeholder="Extra CSS (optioneel), bv .content h1 { color: #0f172a; }"
                style={{ minHeight: '100px', fontFamily: 'monospace' }}
              />
            </>
          )}

          <div className="form-actions">
            <button className="btn-primary" type="submit">{t('common.save')}</button>
            <button className="btn-secondary" type="button" onClick={previewTemplateForm} disabled={!templateForm.bodyTemplate || previewLoading}>
              {previewLoading ? 'Laden…' : 'Voorbeeld'}
            </button>
            <button className="btn-secondary" type="button" onClick={() => setShowTemplateModal(false)}>{t('common.cancel')}</button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={!!preview}
        onClose={() => setPreview(null)}
        title={preview?.title || 'Voorbeeld'}
        size="lg"
      >
        {preview && (
          <div className="declaration-preview">
            <div className="form-row" style={{ alignItems: 'center', marginBottom: '10px' }}>
              <select
                className="form-input"
                value={previewStudentId}
                onChange={(e) => {
                  setPreviewStudentId(e.target.value);
                  loadPreview(preview, e.target.value);
                }}
              >
                <option value="">Voorbeeldstudent (fictieve gegevens)</option>
                {assignableStudents.map((student) => (
                  <option key={student.id || student._id} value={student.id || student._id}>{student.name}</option>
                ))}
              </select>
              <button type="button" className="btn-secondary" onClick={printPreview}>Printen</button>
            </div>
            <small style={{ color: '#4b5563', display: 'block', marginBottom: '8px' }}>
              Lege velden worden met een voorbeeldwaarde tussen [haken] getoond. {previewLoading ? 'Bijwerken…' : ''}
            </small>
            <div style={{ background: '#e5e7eb', padding: '12px', borderRadius: '8px', overflow: 'auto' }}>
              <iframe
                id="declaration-preview-frame"
                title="Voorbeeld document"
                srcDoc={preview.html}
                style={{ display: 'block', width: '210mm', maxWidth: '100%', height: '297mm', margin: '0 auto', border: 0, background: '#fff', boxShadow: '0 2px 10px rgba(0,0,0,0.15)' }}
              />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AdminDeclarations;
